import { NavLink, useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { useState } from 'react';
import { colors } from '../styles/colors.ts';
import { trackEvent } from '../utils/analytics';

const GlobalStyle = createGlobalStyle`
  @keyframes radius {
    from {
      border-radius: 1.5rem;
    }
    50% {
      border-radius: 2rem;
    }
    to {
      border-radius: 1.5rem;
    }
  }

  @keyframes linkOpacity {
    from {
      opacity: 0;
    } to {
      opacity: 1;
    }
  }

  @keyframes linkOut {
    from {
      opacity: 1;
    } to {
      opacity: 0;
    }
  }

`;

const Nav = styled.nav`
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  padding: 1rem;
  z-index: 100;
  display: flex;
  justify-content: space-between;
`;

const Logo = styled.button`
  position: absolute;
  background-color: ${(colors.white)};
  border-radius: 5rem;
  top: 3rem;
  left: 3rem;
  z-index: 101;
  height: 2rem;
  width: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 0;

  & svg {
    pointer-events: none;
  }

  @media (max-width: 768px) {
    left: 1.5rem;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  background: ${colors.neutral[200]}50;
  border-top: solid 1px ${colors.neutral[200]};
  -webkit-backdrop-filter: blur(1rem);
  backdrop-filter: blur(1rem);
  transform: translateY(${({ isOpen }) => (isOpen ? '0' : 'calc(100% + 10rem)')});
  transition: transform 1s ease;
  transition-delay: ${({ isOpen }) => (isOpen ? '0' : '1s')};;
  pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
  z-index: 90;
`;

const NavList = styled.ul`
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  background: ${colors.white};
  border-top: solid 1px ${colors.neutral[200]};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 100;
  margin: 0;
  list-style: none;
  overflow-y: scroll;
  transform: translateY(${({ isOpen }) => (isOpen ? '0' : 'calc(100% + 10rem)')});
  pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
  transition: transform 1s ease;
  transition-delay: ${({ isOpen }) => (isOpen ? '0.5s' : '0s')};
`;

const NavItem = styled.li`
  margin: 1rem 0;
  opacity: ${({ isOpen }) => (isOpen ? '1' : '0')};
  transition: opacity 0.5s ease;
  transition-delay: ${({ isOpen, index }) => isOpen ? `${1 + index * 0.2}s` : `${0.3 + index * 0.2}s`};
`;

const ButtonBase = styled.button`
  color: ${colors.black};
  border-radius: 1.5rem;
  background: ${colors.white};
  height: 4rem;
  width: 4rem;
  border: none;
  font-weight: lighter;
  padding: 1rem;
  z-index: 101;
  cursor: pointer;
  backdrop-filter: blur(12px);
  transition: background .8s, color .8s;
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    padding: 1px;
    border-radius: 1.5rem;
    background: linear-gradient(45deg, #DDDDDD, #CDCFD1, #FFFFFF);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0) border-box;
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    z-index: 1;
    transition: transform 1s;
  }
  & > * {
    font-size: 1.5rem;
    line-height: 2rem;
    pointer-events: none;
  }
  &:hover {
    background: ${colors.neutral[100]};
  }
  &:hover::before {
    animation: radius 1s;
    transform: rotate(360deg);
  }
`;

const HamburgerButton = styled(ButtonBase)`
  opacity: ${({ isOpen }) => (isOpen ? '0' : '1')};
  transition: opacity .2s;
  position: absolute;
  pointer-events: ${({ isOpen }) => (isOpen ? 'none' : 'auto')};
`;

const CloseButton = styled(ButtonBase)`
  opacity: ${({ isOpen }) => (isOpen ? '1' : '0')};
  transition: opacity 0.5s ease;
  transition-delay: ${({ isOpen }) => (isOpen ? '2s' : '0.2s')};
  pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
`;

const StyledNavLink = styled(NavLink)`
  color: ${colors.black};
  text-decoration: none;
  font-size: 4rem;
  font-weight: lighter;
  padding: 1rem;
  transition: color 0.2s, font-weight 0.2s, letter-spacing 0.2s;

  &.active {
    color: ${colors.blue};
    font-weight: 400;
  }

  &:hover {
    color: ${colors.blue};
    font-weight: 400;
    letter-spacing: 0.1rem;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

// Navigation links
const navLinks = [
  { to: '/', label: 'About' },
  { to: '/projects/investifi', label: 'Latest Case Study' },
];

function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const openNav = () => {
    setIsOpen(true);
    trackEvent('navigation_open', 'Navigation');
  };
  
  const closeNav = () => {
    setIsOpen(false);
    trackEvent('navigation_close', 'Navigation');
  };
  
  const goToIntro = () => {
    navigate('/');
    trackEvent('logo_click', 'Navigation');
  };

  return (
    <>
      <GlobalStyle />
      <Overlay isOpen={isOpen} />
      <Logo onClick={goToIntro}>
        <svg width="24" height="24" viewBox="0 0 24 24">
          <path d="M15.702 12C15.702 14.043 14.046 15.699 12.003 15.699C9.96 15.699 8.304 14.043 8.304 12C8.304 9.957 9.957 8.301 12 8.301C13.023 8.301 13.947 8.715 14.616 9.384L16.746 7.254C15.531 6.039 13.854 5.289 12 5.289C8.295 5.289 5.289 8.295 5.289 12C5.289 15.705 8.295 18.711 12 18.711C14.745 18.711 17.106 17.064 18.144 14.703H20.619C19.47 18.369 16.047 21.03 12 21.03C7.011 21.03 2.97 16.989 2.97 12C2.97 7.011 7.011 2.97 12 2.97C14.493 2.97 16.752 3.981 18.387 5.616L20.487 3.516C18.315 1.344 15.315 0 12 0C5.373 0 0 5.373 0 12C0 18.627 5.373 24 12 24C18.627 24 24 18.627 24 12H15.702Z" fill={colors.black}/>
        </svg>
      </Logo>
      <Nav>
        <HamburgerButton onClick={openNav} isOpen={isOpen}>
          <span>+</span>
        </HamburgerButton>
        <NavList isOpen={isOpen}>
          <CloseButton onClick={closeNav} isOpen={isOpen}>
            <span>×</span>
          </CloseButton>
          {navLinks.map(({ to, label }, index) => (
            <NavItem key={to} isOpen={isOpen} index={index}>
              <StyledNavLink
                to={to}
                onClick={() => {
                  closeNav();
                  trackEvent('nav_link_click', 'Navigation', label);
                }}
                className={({ isActive }) => (isActive ? 'active' : '')}
              >
                {label}
              </StyledNavLink>
            </NavItem>
          ))} 
        </NavList>
      </Nav>
    </>
  );
}

export default Navigation;
